import { JWT_KEY } from "@/constants";
import { loginService } from "@/services/auth.service";
import { ErrorMessage, LoginBody } from "@/types";
import { ROUTE } from "@/types/router";
import { useUserStore } from "@/zustand/user";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import axios from "axios";
import { useFormik } from "formik";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  username: Yup.string().required("Please enter username!"),
  password: Yup.string().required("Please enter password!"),
});

const initialValues = {
  username: "",
  password: "",
};

const LoginPage = () => {
  const [isVisiblePassword, setVisiblePassword] = useState<boolean>(false);
  const navigation = useNavigate();
  const { updateUser } = useUserStore();

  const handleClickShowPassword = () => setVisiblePassword((show) => !show);

  const handleLogin = useCallback(
    async (dataLogin: LoginBody) => {
      try {
        const data = await loginService(dataLogin);
        localStorage.setItem(JWT_KEY, data.accessToken);
        updateUser({ isLogin: true });
        navigation(ROUTE.Home, { replace: true });
        toast.success("Login successfully.");
      } catch (error: unknown) {
        if (axios.isAxiosError<ErrorMessage>(error)) {
          toast.error(
            error.response?.data?.message || "Login failed. Please try again.",
          );
        } else {
          toast.error("Login failed. Please try again.");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { values, handleChange, submitForm, errors } = useFormik<LoginBody>({
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: handleLogin,
  });

  return (
    <div
      className="flex min-h-dvh flex-row max-md:flex-col"
      style={{
        background: "linear-gradient(180deg, #B6E0E4 0%, #85E8EB 37.78%)",
      }}
    >
      <div className="flex items-center justify-center bg-[#334155] max-md:h-[220px] max-md:w-full md:w-[33.051%]">
        <img
          src="/images/logo.png"
          alt="logo"
          className="h-[96px] w-[268px] object-contain"
        />
      </div>

      <div className="col-span-8 h-full flex-1 bg-[#F6F9FB] px-5 py-10 md:flex md:h-dvh md:items-center md:justify-center">
        <div className="flex  flex-col items-center justify-center">
          <h2 className="mb-11 text-center text-xl font-medium text-[#020617]">
            Welcome to Asia Experience Dashboard
          </h2>
          <div className="w-full">
            <div className="mb-4 flex flex-col">
              <label htmlFor="username" className="mb-2">
                <span className="text-sm font-semibold text-[#1E293B]">
                  Email address
                </span>
              </label>
              <input
                id="username"
                name="username"
                type="text"
                value={values.username}
                onChange={handleChange}
                placeholder="Enter your Asia Experience company email"
                className="h-[48px] w-[476px] rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#020617] outline-none placeholder:text-sm placeholder:font-normal placeholder:text-[#94A3B8] max-lg:w-[353px] max-md:w-full"
              />
              {errors.username && (
                <p className="text-[12px] text-red-600">{errors.username}</p>
              )}
            </div>

            <div className="flex flex-col">
              <label htmlFor="password" className="mb-2">
                <span className="text-sm font-semibold text-[#1E293B]">
                  Password
                </span>
              </label>

              <div className="flex h-[48px] w-[476px] items-center justify-between rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#94A3B8] outline-none placeholder:text-sm placeholder:font-normal max-lg:w-[353px] max-md:w-full">
                <input
                  id="password"
                  name="password"
                  type={isVisiblePassword ? "text" : "password"}
                  value={values.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="w-full !bg-transparent text-[#020617] outline-none placeholder:text-sm placeholder:font-normal placeholder:text-[#94A3B8]"
                />

                <div className="cursor-pointer">
                  {isVisiblePassword ? (
                    <VisibilityOutlinedIcon onClick={handleClickShowPassword} />
                  ) : (
                    <VisibilityOffOutlinedIcon
                      onClick={handleClickShowPassword}
                    />
                  )}
                </div>
              </div>

              {!errors.password && (
                <p className="text-[12px] text-red-600">{errors.password}</p>
              )}
            </div>

            <button
              onClick={submitForm}
              className="relative mt-8 flex w-full justify-center rounded-md bg-[#020617] px-3 py-2 text-base font-medium text-white disabled:bg-[#EAF0F6] disabled:text-[#94A3B8]"
              disabled={!values.username || !values.password}
            >
              <span>Login</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
